import { useState, useEffect, useRef, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  ProgressBar,
  Row,
  Table,
} from "react-bootstrap";
import { HelpModal } from "../../common/compoents/HelpModal";
import { IoMdPlay } from "react-icons/io";
import { FaDollarSign, FaExclamationTriangle, FaEye } from "react-icons/fa";
import { IoLogoGoogle } from "react-icons/io5";
import { GiCycle } from "react-icons/gi";
import { AuditDataModel, IPageView } from "../types";
import { SeoWebsiteAuditView } from "./SeoWebsiteAuditView";
import { crawlWebsite } from "../utils/seoAnalyzer";
import { useSeoAuditData } from "../hooks/useSeoAuditData";
import { useAuditStore } from "../store/useAuditStore";
import {
  criticalListLeft,
  warningListLeft,
  noticesListLeft,
} from "../constants";
import "../audit_style.css";
import { IssueSidebar } from "./IssueSidebar";
import { WebsiteScoreCard } from "./WebsiteScoreCard";
import { useNavigate } from "react-router-dom";
import { ToolsHeader } from "../../components/ToolsHeader";
import { isInIframe } from "../../../utils/is-in-iframe";

export const SeoWebsiteAudit: React.FC = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [siteTitle, setSiteTitle] = useState<string | null>(null);
  const [urlText, setUrlText] = useState("");
  const [selectTab, setSelectTab] = useState("all");
  const [activeSide, setActiveSide] = useState<string | null>(null);
  const [viewPage, setViewPage] = useState<IPageView | null>(null);
  const [indexedPages, setIndexedPages] = useState(0);
  const [collectionData, setCollectionData] = useState<AuditDataModel>({
    missingTitles: [],
    url: "",
    metaTags: {
      title: "",
      description: "",
      canonical: "",
      robots: "",
      issues: [],
    },
    headings: { h1: [], h2: [], h3: [], issues: [] },
    images: [],
    technical: {
      internalLinks: [],
      brokenLinks: [],
      structuredData: null,
      issues: [],
    },
    performance: {
      loadTime: 0,
      mobileOptimized: false,
      coreWebVitals: { lcp: 0, fid: 0, cls: 0 },
      issues: [],
    },
    security: {
      https: false,
      mixedContent: false,
      sslValid: false,
      issues: [],
    },
    score: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const tableRef = useRef<HTMLDivElement>(null);

  const { audits, setAuditData } = useAuditStore();
  const {
    criticalCollected,
    warningCollected,
    noticesCollected,
    passedCollected,
    criticalPercentage,
    warningPercentage,
    noticesPercentage,
    passedPercentage,
    checkSafe,
  } = useSeoAuditData(collectionData);

  const calculateWebsiteScore = useCallback(() => {
    if (!collectionData || collectionData.missingTitles.length === 0) return 0;
    const totalScore = collectionData.missingTitles.reduce(
      (sum, page) => sum + page.score,
      0
    );
    const averageScore = totalScore / collectionData.missingTitles.length;
    return Math.round(averageScore);
  }, [collectionData]);

  const websiteScore = calculateWebsiteScore();
  const navigate = useNavigate();

  const helpSteps = [
    "Paste the website URL you want to analyze into the input field.",
    "Click 'Analyze Website' to see initial results quickly.",
    "Scroll down to load more pages automatically.",
  ];

  const fetchInitialData = useCallback(async () => {
    if (!urlText) return;

    const fullUrl = "https://" + urlText;
    setIsLoading(true);
    setCurrentPage(1);
    setHasMore(true);
    setCollectionData({
      missingTitles: [],
      url: "",
      metaTags: {
        title: "",
        description: "",
        canonical: "",
        robots: "",
        issues: [],
      },
      headings: { h1: [], h2: [], h3: [], issues: [] },
      images: [],
      technical: {
        internalLinks: [],
        brokenLinks: [],
        structuredData: null,
        issues: [],
      },
      performance: {
        loadTime: 0,
        mobileOptimized: false,
        coreWebVitals: { lcp: 0, fid: 0, cls: 0 },
        issues: [],
      },
      security: {
        https: false,
        mixedContent: false,
        sslValid: false,
        issues: [],
      },
      score: 0,
    });

    if (audits[fullUrl]) {
      const cachedData = audits[fullUrl];
      if (cachedData) {
        setCollectionData(
          cachedData.collectionData ?? {
            missingTitles: [],
            url: "",
            metaTags: {
              title: "",
              description: "",
              canonical: "",
              robots: "",
              issues: [],
            },
            headings: { h1: [], h2: [], h3: [], issues: [] },
            images: [],
            technical: {
              internalLinks: [],
              brokenLinks: [],
              structuredData: null,
              issues: [],
            },
            performance: {
              loadTime: 0,
              mobileOptimized: false,
              coreWebVitals: { lcp: 0, fid: 0, cls: 0 },
              issues: [],
            },
            security: {
              https: false,
              mixedContent: false,
              sslValid: false,
              issues: [],
            },
            score: 0,
          }
        );
        setIndexedPages(cachedData.indexedPages);
        setSiteTitle(cachedData.siteTitle);
        setHasMore(false);
      }
      setIsLoading(false);
      return;
    }

    try {
      const initialResult = await crawlWebsite(fullUrl, 10);
      setCollectionData(initialResult);
      setSiteTitle(urlText);
      setCurrentPage(2);
      await checkIndexedPages(fullUrl);
    } catch (error) {
      console.log("Error fetching initial audit data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [urlText, audits]);

  const fetchMoreData = useCallback(async () => {
    if (!urlText || isLoadingMore || !hasMore) return;

    const fullUrl = "https://" + urlText;
    setIsLoadingMore(true);

    try {
      const offset = (currentPage - 1) * 10;
      const nextResult = await crawlWebsite(fullUrl, 10);

      setCollectionData((prev) => ({
        ...prev,
        missingTitles: [...prev.missingTitles, ...nextResult.missingTitles],
      }));

      setCurrentPage((prev) => prev + 1);

      if (
        nextResult.missingTitles.length < 10 ||
        collectionData.missingTitles.length + nextResult.missingTitles.length >=
          100
      ) {
        setHasMore(false);

        setAuditData(fullUrl, {
          collectionData: {
            ...collectionData,
            missingTitles: [
              ...collectionData.missingTitles,
              ...nextResult.missingTitles,
            ],
          },
          indexedPages,
          siteTitle: urlText,
        });
      }
    } catch (error) {
      console.log("Error fetching more audit data:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    urlText,
    currentPage,
    collectionData,
    indexedPages,
    isLoadingMore,
    hasMore,
    setAuditData,
  ]);

  useEffect(() => {
    if (!tableRef.current || isLoading || isLoadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchMoreData();
        }
      },
      { threshold: 0.1, root: tableRef.current }
    );

    const observerElement = document.getElementById("observer-element");
    if (observerElement) {
      observer.observe(observerElement);
    }

    return () => {
      if (observerElement) {
        observer.unobserve(observerElement);
      }
    };
  }, [fetchMoreData, isLoading, isLoadingMore, hasMore]);

  const checkIndexedPages = async (url: string) => {
    try {
      const response = await fetch(
        `https://development.mydevelopmentserver.io/api/v1/proxy-data`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: `https://serpapi.com/search.json?engine=google&q=site:${url}&api_key=ea15558d39e42c44674e38a2da13860ad9e6e02e9c50ac824a7999667d69fe53`,
            responseType: "json",
          }),
        }
      );
      const data = await response.json();
      if (
        data.statusCode === 200 &&
        data?.data?.search_information?.total_results !== undefined
      ) {
        const newIndexedPages = data.data.search_information.total_results || 0;
        setIndexedPages(newIndexedPages);
        if (audits[url]) {
          setAuditData(url, {
            ...audits[url],
            indexedPages: newIndexedPages,
          });
        }
        return newIndexedPages;
      }
    } catch (error) {
      console.log("Error checking indexed pages:", error);
    }
  };

  return (
    <Row style={{ padding: "0px" }}>
      <Col lg={12}>
        <HelpModal
          helpSteps={helpSteps}
          show={showVideoModal}
          close={() => setShowVideoModal(false)}
          videoUrl="https://www.youtube.com/embed/hNce9hFpBOY"
        />
        <Modal
          show={showCalculationModal}
          onHide={() => setShowCalculationModal(false)}
          centered
          className="custom-modal"
          dialogClassName="custom-modal-dialog"
        >
          <Modal.Header closeButton>
            <Modal.Title>How is it calculated?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>We use the following formula to calculate this metric.</p>
            <div className="formula-box">
              <p>
                <GiCycle className="icon" />
                <strong>Website Score</strong> = sum of score of crawled pages /
                # of crawled pages
              </p>
              <p>
                <GiCycle className="icon" />
                <strong>OnePageScore</strong> = ((high issues + medium issues) /
                (130 + 50)) * 100
              </p>
              <p>
                <GiCycle className="icon" />
                <strong>*Note</strong>: high issues = (130 - number of high
                issues * 10)
              </p>
              <p>
                <GiCycle className="icon" />
                medium issues = (50 - number of medium issues * 5)
              </p>
              <p>
                <FaDollarSign className="icon dollar-icon" />
                <strong>Cost of specific critical error</strong> = 10
              </p>
              <p>
                <FaExclamationTriangle className="icon warning-icon" />
                <strong>Cost of specific warning</strong> = 05
              </p>
            </div>
            <p className="dive-text">
              Let's dive into each element of formula.
            </p>
          </Modal.Body>
        </Modal>
        {!isInIframe && (
          <ToolsHeader
            title="SEO Website Audit"
            description="Detect AI-generated content and evaluate its SEO quality for
                better performance insights."
          />
        )}
        {/* <h1 className="text-center mb-3">SEO Website Audit</h1>
        <p className="text-center mb-4">
          Analyze your website to assess its SEO optimization quality across
          multiple pages.
        </p> */}
        {/* <div className="d-flex justify-content-center mb-4">
          <Button
            variant="outline-primary d-flex justify-content-center align-items-center"
            size="sm"
            onClick={() => setShowVideoModal(true)}
          >
            See how to use <IoMdPlay className="ms-2" />
          </Button>
        </div> */}
        <div
          className="d-flex justify-content-center mb-4"
          style={{ cursor: "pointer", color: "#327aee" }}
          onClick={() => navigate("/audit-overview")}
        >
          Overview
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <InputGroup style={{ maxWidth: "500px" }}>
            <InputGroup.Text>https://</InputGroup.Text>
            <Form.Control
              placeholder="Enter website URL"
              value={urlText}
              onChange={(e) =>
                setUrlText(e.target.value.replace("https://", ""))
              }
            />
            <Button
              variant="primary"
              onClick={fetchInitialData}
              disabled={isLoading}
            >
              {isLoading ? "Analyzing..." : "Analyze Website"}
            </Button>
          </InputGroup>
        </div>
        {isLoading && (
          <div className="text-center mt-3">
            <p>Analyzing initial pages...</p>
            <ProgressBar
              animated
              now={100}
              style={{ maxWidth: "200px", margin: "0 auto" }}
            />
          </div>
        )}
        {viewPage === null && (
          <div>
            {siteTitle && (
              <WebsiteScoreCard
                websiteScore={websiteScore}
                siteTitle={siteTitle}
                crawledPages={collectionData?.missingTitles.length || 0}
                indexedPages={indexedPages}
                safeBrowsingStatus={checkSafe()}
                selectTab={selectTab}
                criticalCollected={criticalCollected}
                warningCollected={warningCollected}
                noticesCollected={noticesCollected}
                passedCollected={passedCollected}
                criticalPercentage={criticalPercentage}
                warningPercentage={warningPercentage}
                noticesPercentage={noticesPercentage}
                passedPercentage={passedPercentage}
                setSelectTab={setSelectTab}
                setActiveSide={setActiveSide}
                setShowCalculationModal={setShowCalculationModal}
              />
            )}
            {collectionData.missingTitles.length > 0 && (
              <Row>
                <IssueSidebar
                  selectTab={selectTab}
                  activeSide={activeSide}
                  criticalCollected={criticalCollected}
                  warningCollected={warningCollected}
                  noticesCollected={noticesCollected}
                  passedCollected={passedCollected}
                  setSelectTab={setSelectTab}
                  setActiveSide={setActiveSide}
                />
                <Col lg={9}>
                  <Card
                    className="p-2 mt-3"
                    style={{
                      maxWidth: "100%",
                      boxShadow:
                        "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
                    }}
                  >
                    <div
                      ref={tableRef}
                      style={{
                        maxHeight: "520px",
                        overflowY: "auto",
                      }}
                    >
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>URL</th>
                            <th>Result</th>
                            <th></th>
                            <th>Generated At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {collectionData?.missingTitles.map(
                            (item: any, index: number) => (
                              <tr key={index}>
                                <td>
                                  <a
                                    href={item.canonical}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="linkTable"
                                  >
                                    {item.canonical}
                                  </a>
                                  <div style={{ color: "#327aee" }}>
                                    {item.title}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#313131",
                                    }}
                                  >
                                    {item.description}
                                  </div>
                                  <div>
                                    {item.issues.map(
                                      (a: any, i: number) =>
                                        criticalListLeft.find(
                                          (t: { name: string }) => t.name === a
                                        ) && (
                                          <Badge
                                            key={i}
                                            bg="danger"
                                            style={{ marginRight: "5px" }}
                                          >
                                            {a}
                                          </Badge>
                                        )
                                    )}
                                    {item.issues.map(
                                      (a: any, i: number) =>
                                        warningListLeft.find(
                                          (t: { name: string }) => t.name === a
                                        ) && (
                                          <Badge
                                            key={i}
                                            bg="warning"
                                            style={{ marginRight: "5px" }}
                                          >
                                            {a}
                                          </Badge>
                                        )
                                    )}
                                    {item.issues.map(
                                      (a: any, i: number) =>
                                        noticesListLeft.find(
                                          (t: { name: string }) => t.name === a
                                        ) && (
                                          <Badge
                                            key={i}
                                            bg="secondary"
                                            style={{ marginRight: "5px" }}
                                          >
                                            {a}
                                          </Badge>
                                        )
                                    )}
                                  </div>
                                </td>
                                <td style={{ width: "6rem" }}>
                                  <ProgressBar
                                    now={item.score}
                                    style={{ height: "7px" }}
                                    variant="success"
                                  />
                                  <div>
                                    <span>{item.score}</span>/100
                                  </div>
                                  <a
                                    style={{
                                      color: "#198754",
                                      backgroundColor: "rgba(40,167,69,0.2)",
                                    }}
                                  >
                                    Good
                                  </a>
                                </td>
                                <td style={{ width: "9rem" }}>
                                  <div
                                    style={{
                                      color: "#4b4949",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        marginRight: "0.3rem",
                                        marginTop: "-5px",
                                      }}
                                    >
                                      <IoLogoGoogle />
                                    </div>
                                    Indexable
                                  </div>
                                </td>
                                <td style={{ width: "8rem" }}>
                                  0 week ago
                                  <FaEye
                                    style={{
                                      marginLeft: "10px",
                                      cursor: "pointer",
                                      color: "#327aee",
                                    }}
                                  />
                                </td>
                              </tr>
                            )
                          )}
                          {hasMore && (
                            <tr id="observer-element">
                              <td colSpan={4} className="text-center">
                                {isLoadingMore ? (
                                  <div className="d-flex justify-content-center align-items-center">
                                    <div
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                    Loading more results...
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}
          </div>
        )}
        {viewPage !== null && (
          <SeoWebsiteAuditView viewPage={viewPage} setViewPage={setViewPage} />
        )}
      </Col>
    </Row>
  );
};
